import type { OddsFormat } from "@/utils/calculators/odds-utils";
import { americanToDecimal, formatCentsOdds } from "@/utils/calculators/odds-utils";
import { buildOppositeLabel, getSideLabels } from "../odds-labels";
import { isOverSlotSide, isUnderSlotSide, isYesNoSide, outcomeLabel } from "@/lib/selection-side";
import { getPick6Multiplier, getUnderdogMultiplier } from "./odds";
import { signedLine, trimLine } from "@/components/odds-screen/live/labels";
import { SPREAD_MARKET } from "@/lib/market-label";
import type { DisplayedOddsResult, DisplayPlayerProp, PlatformType } from "./types";
import { bookStake, type Stake, type StakeQuote } from "@/lib/book-stake";
import type { SideNames, WhalesSpec } from "@/lib/whales";

export function bookLiquidity(row: DisplayPlayerProp, book: string, side: "bet" | "opposite" = "bet"): number | null {
  const pair = row.__sportsbook_liquidity?.[book];
  const underSlot = side === "bet" ? isUnderSlotSide(row.bet_type) : !isUnderSlotSide(row.bet_type);
  const amount = underSlot ? pair?.under : pair?.over;
  return typeof amount === "number" && Number.isFinite(amount) ? amount : null;
}

function rowSideNames(row: DisplayPlayerProp): SideNames {
  const outcome = outcomeLabel(row.outcome_key);
  if (outcome !== null) return { side: outcome, other: null };
  if (!isOverUnderBet(row.bet_type)) return { side: null, other: null };
  const { selectionLabel, oppositeLabel } = getSideLabels(row.bet_type);
  return { side: selectionLabel, other: oppositeLabel };
}

const isGameSide = (row: DisplayPlayerProp) => outcomeLabel(row.outcome_key) !== null || /^(home|away)$/i.test(row.outcome_key ?? "");

export function rowStakeQuote(row: DisplayPlayerProp, book: PlatformType, odds: number | null): StakeQuote | null {
  const stake = bookStake(book, bookLiquidity(row, book));
  if (stake === null || odds === null || !Number.isFinite(odds) || odds === 0) return null;
  return { stake, odds, ...rowSideNames(row) };
}

function whalesLine(row: DisplayPlayerProp, which: "side" | "opp", names: SideNames): string | null {
  if (typeof row.line !== "number" || isYesNoSide(row.bet_type)) return null;
  const name = which === "side" ? names.side : names.other;
  let at = trimLine(Math.abs(row.line));
  if (isGameSide(row)) {
    if (!SPREAD_MARKET.test(row.stat_type ?? "")) return null;
    at = signedLine(which === "side" ? row.line : -row.line);
  }
  return name === null ? `the ${at} line` : `${name} ${at}`;
}

export function rowWhalesSpec(row: DisplayPlayerProp, which: "side" | "opp"): WhalesSpec | null {
  if (row.whales == null) return null;
  return { cell: row.whales, which, line: whalesLine(row, which, rowSideNames(row)) };
}

export function oppositeStake(row: DisplayPlayerProp, book: string): Stake | null {
  return bookStake(book, bookLiquidity(row, book, "opposite"));
}


function liveMultiplier(row: DisplayPlayerProp, isPrimary: boolean | undefined): number | null | undefined {
  if (row.grading === undefined) return undefined;
  return isPrimary ? row.payout_multiplier ?? 1 : null;
}

function isOverUnderBet(betType: string | null | undefined): boolean {
  if (!betType) return false;
  const upper = betType.toUpperCase();
  return upper === "OVER" || upper === "UNDER" || upper === "YES" || upper === "NO";
}

function formatAmericanOddsForDisplay(
  americanOdds: number,
  oddsFormat: OddsFormat,
): string | null {
  if (!Number.isFinite(americanOdds) || americanOdds === 0) return null;

  if (oddsFormat === "decimal") {
    const decimal = americanToDecimal(americanOdds);
    if (!Number.isFinite(decimal) || decimal <= 1) return null;
    return decimal.toFixed(2);
  }

  if (oddsFormat === "cents") {
    const decimal = americanToDecimal(americanOdds);
    if (!Number.isFinite(decimal) || decimal <= 1) return null;
    const cents = formatCentsOdds(decimal);
    return cents || null;
  }

  return americanOdds > 0 ? `+${americanOdds}` : String(americanOdds);
}

export function getOppositeOddsText(
  row: DisplayPlayerProp,
  bookId: string,
  options?: { oddsFormat?: OddsFormat },
): { oppositeSide: string; oddsText: string } | null {
  if (outcomeLabel(row.outcome_key) !== null) return null;
  const betTypeUpper = (row.bet_type || "").toUpperCase();
  const isOverUnder = isOverUnderBet(betTypeUpper);
  const isHomeAway = betTypeUpper === "HOME" || betTypeUpper === "AWAY";
  const oddsFormat = options?.oddsFormat ?? "american";

  if (!isOverUnder && !isHomeAway) return null;

  const isOverSelection = isOverSlotSide(betTypeUpper);
  const oppositeSide = isYesNoSide(betTypeUpper)
    ? getSideLabels(betTypeUpper).oppositeLabel
    : isOverSelection
      ? "U"
      : "O";

  const oppositeField = isOverSelection
    ? `${bookId}_under_odds`
    : `${bookId}_over_odds`;

  const oppositeOdds = row[oppositeField as keyof DisplayPlayerProp];

  if (oppositeOdds == null) return null;
  const n = Number(oppositeOdds);
  const oddsText = formatAmericanOddsForDisplay(n, oddsFormat);
  if (!oddsText) return null;

  if (isHomeAway) {
    const { oppositeLabel } = getSideLabels(betTypeUpper);
    const label = buildOppositeLabel(row, oppositeLabel);
    return { oppositeSide: label, oddsText };
  }

  return { oppositeSide, oddsText };
}


export function getDisplayedOdds(
  row: DisplayPlayerProp,
  columnKey: string,
  options?: {
    isPrimary?: boolean;
    isComparison?: boolean;
    hasBetterOdds?: boolean;
    defaultOdds?: number;
    bookId?: PlatformType;
    oddsFormat?: OddsFormat;
  }
): DisplayedOddsResult {
  const result: DisplayedOddsResult = {
    oddsText: null,
    isMissing: true,
    lineBadge: null,
    stake: null,
    oppositeStake: null,
    multiplierText: null,
    colorVariant: 'subdued',
    oppositeOddsText: null,
    oppositeSide: null,
  };

  const rawValue = row[columnKey as keyof DisplayPlayerProp];
  const slipOdds = options?.isPrimary ? row.slip_implied_odds : null;
  const posted = rawValue ?? options?.defaultOdds ?? null;
  const odds = slipOdds ?? posted;

  if (columnKey === "underdog_odds" || columnKey === "underdog_direct_odds") {
    const n = posted != null ? Number(posted) : null;
    if (n != null && Number.isFinite(n) && n !== 0) {
      const live = liveMultiplier(row, options?.isPrimary);
      const mult = live === undefined ? getUnderdogMultiplier(n) : live;
      if (mult !== null && mult !== 1.0) {
        result.multiplierText = `${mult.toFixed(2)}x`;
        result.isMissing = false;
        result.colorVariant = options?.isPrimary ? 'primary' : 'comparison';
      }
    }
  }

  if (columnKey === "dkpick6_odds") {
    const n = posted != null ? Number(posted) : null;
    if (n != null && Number.isFinite(n) && n !== 0) {
      const live = liveMultiplier(row, options?.isPrimary);
      const mult = live === undefined ? getPick6Multiplier(n) : live;
      if (mult !== null && mult !== 1.0) {
        result.multiplierText = `${mult.toFixed(2)}x`;
        result.isMissing = false;
        result.colorVariant = options?.isPrimary ? 'primary' : 'comparison';
      }
    }
  }

  if (!result.multiplierText && odds != null) {
    const n = Number(odds);
    if (Number.isFinite(n) && n !== 0) {
      const oddsFormat = options?.oddsFormat ?? "american";
      const oddsText = formatAmericanOddsForDisplay(n, oddsFormat);
      if (oddsText) {
        result.oddsText = oddsText;
        result.isMissing = false;
      }
    }
  }

  if (!result.isMissing) {
    if (options?.isPrimary) {
      result.colorVariant = 'primary';
    } else if (options?.hasBetterOdds) {
      result.colorVariant = 'better';
    } else if (options?.isComparison) {
      result.colorVariant = 'subdued';
    } else {
      result.colorVariant = 'comparison';
    }
  }

  const bookId = options?.bookId;
  if (bookId && row.__comparison_lines) {
    const lineVal = row.__comparison_lines[bookId];
    if (typeof lineVal === "number" && Number.isFinite(lineVal)) {
      result.lineBadge = trimLine(lineVal);
    }
  }

  if (bookId) {
    result.stake = result.isMissing || posted == null ? null : rowStakeQuote(row, bookId, Number(posted));
    const opposite = getOppositeOddsText(row, bookId, { oddsFormat: options?.oddsFormat });
    if (opposite) {
      result.oppositeStake = oppositeStake(row, bookId);
      const slipText = slipOdds == null ? null : formatAmericanOddsForDisplay(slipOdds, options?.oddsFormat ?? "american");
      result.oppositeOddsText = slipText ?? opposite.oddsText;
      result.oppositeSide = opposite.oppositeSide;
    }
  }

  return result;
}

export function getRowKey(row: DisplayPlayerProp): string {
  const gameId = row.game_id;
  const playerId = row.player_id;
  const statType = row.stat_type;
  const line = row.line ?? '';
  const betType = row.bet_type ?? '';
  const primaryBook = row.primary_book ?? '';

  const hasGameId = gameId !== null && gameId !== undefined;
  const hasPlayerId = playerId !== null && playerId !== undefined;
  const hasStatType = typeof statType === 'string' && statType.length > 0;

  if (hasGameId && hasPlayerId && hasStatType) {
    return `${gameId}-${playerId}-${statType}-${line}-${betType}-${primaryBook}`;
  }

  if (row.id != null && row.id !== '') {
    return `${row.id}-${betType}-${primaryBook}`;
  }

  return `fallback-${gameId ?? 'nogame'}-${playerId ?? 'noplayer'}-${statType ?? 'nostat'}-${row.player_name ?? 'unknown'}-${line}-${betType}-${primaryBook}`;
}
